/* LH Store — global behaviors */

// Theme selection: match lh-website behavior with localStorage + system preference fallback
(() => {
  const storageKey = 'lh-theme';
  const root = document.documentElement;
  const themeColorMeta = document.querySelector('meta[name="theme-color"]');

  const isValidTheme = (value) => value === 'light' || value === 'dark';
  const getImplicitPreference = () =>
    window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';

  const getTheme = () => {
    const documentTheme = root.getAttribute('data-theme');
    if (isValidTheme(documentTheme)) return documentTheme;

    const storedTheme = window.localStorage.getItem(storageKey);
    if (isValidTheme(storedTheme)) return storedTheme;

    return getImplicitPreference();
  };

  const updateToggleUi = (theme) => {
    document.querySelectorAll('[data-theme-toggle]').forEach((button) => {
      const nextTheme = theme === 'light' ? 'dark' : 'light';
      const label = `Switch to ${nextTheme} mode`;
      button.setAttribute('aria-label', label);

      const labelNode = button.querySelector('[data-theme-toggle-label]');
      if (labelNode) labelNode.textContent = label;
    });
  };

  const updateThemeColor = (theme) => {
    if (!themeColorMeta) return;

    const color =
      theme === 'dark'
        ? themeColorMeta.getAttribute('data-dark-color')
        : themeColorMeta.getAttribute('data-light-color');

    if (color) themeColorMeta.setAttribute('content', color);
  };

  const applyTheme = (theme, persist = true) => {
    root.setAttribute('data-theme', theme);
    if (persist) {
      window.localStorage.setItem(storageKey, theme);
    } else {
      window.localStorage.removeItem(storageKey);
    }
    updateToggleUi(theme);
    updateThemeColor(theme);
  };

  const initialTheme = getTheme();
  root.setAttribute('data-theme', initialTheme);
  updateToggleUi(initialTheme);
  updateThemeColor(initialTheme);

  document.querySelectorAll('[data-theme-toggle]').forEach((button) => {
    button.addEventListener('click', () => {
      const currentTheme = getTheme();
      applyTheme(currentTheme === 'light' ? 'dark' : 'light');
    });
  });

  const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
  const onSystemThemeChange = () => {
    if (!isValidTheme(window.localStorage.getItem(storageKey))) {
      applyTheme(getImplicitPreference(), false);
    }
  };

  if (typeof mediaQuery.addEventListener === 'function') {
    mediaQuery.addEventListener('change', onSystemThemeChange);
  } else if (typeof mediaQuery.addListener === 'function') {
    mediaQuery.addListener(onSystemThemeChange);
  }
})();

// Sticky header: transparent at top, glassy after 8px of scroll (matches lh-website header)
(() => {
  const header = document.querySelector('.site-header');
  if (!header) return;

  const onScroll = () => {
    header.classList.toggle('is-scrolled', window.scrollY > 8);
  };

  onScroll();
  window.addEventListener('scroll', onScroll, { passive: true });
})();

// Mobile menu overlay
(() => {
  const menu = document.getElementById('MobileMenu');
  const toggle = document.querySelector('[data-menu-toggle]');
  if (!menu || !toggle) return;

  let scrollY = 0;

  const setOpen = (open) => {
    menu.classList.toggle('is-open', open);
    document.body.classList.toggle('menu-open', open);
    toggle.setAttribute('aria-expanded', String(open));

    if (open) {
      scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.width = '100%';
      document.body.style.overscrollBehavior = 'none';
    } else {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.width = '';
      document.body.style.overscrollBehavior = '';
      window.scrollTo(0, scrollY);
    }
  };

  toggle.addEventListener('click', () => {
    setOpen(!menu.classList.contains('is-open'));
  });

  menu.querySelectorAll('[data-menu-close]').forEach((el) => {
    el.addEventListener('click', () => setOpen(false));
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && menu.classList.contains('is-open')) setOpen(false);
  });
})();

// Quantity steppers
class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.querySelectorAll('button').forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        const previous = this.input.value;
        if (button.name === 'plus') this.input.stepUp();
        if (button.name === 'minus') this.input.stepDown();
        if (previous !== this.input.value) {
          this.input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    });
  }
}
customElements.define('quantity-input', QuantityInput);

// Cart page: submit the cart form when a line quantity changes
class CartQuantity extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('change', () => {
      const form = this.closest('form');
      if (form) form.submit();
    });
  }
}
customElements.define('cart-quantity', CartQuantity);

// Variant picker: match selected options to a variant, update price/availability/URL
class VariantSelects extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('change', this.onVariantChange.bind(this));
  }

  get variantData() {
    this._variantData =
      this._variantData || JSON.parse(this.querySelector('[type="application/json"]').textContent);
    return this._variantData;
  }

  onVariantChange() {
    const selected = Array.from(this.querySelectorAll('select'), (select) => select.value);
    const variant = this.variantData.find((v) =>
      v.options.every((option, index) => option === selected[index])
    );

    const form = document.getElementById(this.dataset.formId);
    const idInput = form ? form.querySelector('input[name="id"]') : null;
    const button = form ? form.querySelector('[name="add"]') : null;
    const buttonText = button ? button.querySelector('span') : null;
    const priceEl = document.getElementById(this.dataset.priceId);

    if (!variant) {
      if (button) {
        button.setAttribute('disabled', 'disabled');
        if (buttonText) buttonText.textContent = this.dataset.textUnavailable;
      }
      return;
    }

    if (idInput) idInput.value = variant.id;

    if (button) {
      if (variant.available) {
        button.removeAttribute('disabled');
        if (buttonText) buttonText.textContent = this.dataset.textAddToCart;
      } else {
        button.setAttribute('disabled', 'disabled');
        if (buttonText) buttonText.textContent = this.dataset.textSoldOut;
      }
    }

    if (priceEl) {
      const format = (cents) =>
        (this.dataset.moneyFormat || '${{amount}}').replace(
          /\{\{\s*\w+\s*\}\}/,
          (cents / 100).toFixed(2)
        );
      if (variant.compare_at_price > variant.price) {
        priceEl.innerHTML =
          '<del>' + format(variant.compare_at_price) + '</del><ins>' + format(variant.price) + '</ins>';
      } else {
        priceEl.textContent = format(variant.price);
      }
    }

    if (variant.featured_media && variant.featured_media.id) {
      const thumb = document.querySelector(
        '[data-media-id="' + variant.featured_media.id + '"]'
      );
      if (thumb) thumb.click();
    }

    window.history.replaceState({}, '', this.dataset.url + '?variant=' + variant.id);
  }
}
customElements.define('variant-selects', VariantSelects);

// Collection product type filtering, sorting, and pagination
class CollectionFilters extends HTMLElement {
  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;

    this.onChange = this.onChange.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
    this.onClick = this.onClick.bind(this);
    this.onPopState = this.onPopState.bind(this);

    this.addEventListener('change', this.onChange);
    this.addEventListener('submit', this.onSubmit);
    this.addEventListener('click', this.onClick);
    window.addEventListener('popstate', this.onPopState);

    this.enhanceControls();

    if (this.shouldHydrateFromLocation()) {
      this.render(this.getCleanUrl(window.location.href), {
        history: 'replace',
        focus: null,
        scroll: false,
        initial: true,
      });
    }
  }

  disconnectedCallback() {
    if (!this._initialized) return;

    this.removeEventListener('change', this.onChange);
    this.removeEventListener('submit', this.onSubmit);
    this.removeEventListener('click', this.onClick);
    window.removeEventListener('popstate', this.onPopState);
    if (this.abortController) this.abortController.abort();
    if (this.announcementFrame) window.cancelAnimationFrame(this.announcementFrame);
    this.productCache = null;
    this._initialized = false;
  }

  enhanceControls() {
    const submitButton = this.querySelector('[data-collection-filter-submit]');
    if (submitButton) submitButton.hidden = true;
  }

  onChange(event) {
    if (!(event.target instanceof Element)) return;

    const control = event.target.closest(
      '[data-collection-filter-input], [data-collection-sort]'
    );
    if (!control || !this.contains(control)) return;

    const form = control.closest('[data-collection-filter-form]');
    if (!form) return;

    this.render(this.getFormUrl(form), {
      history: 'push',
      focus: this.getFocusDescriptor(control),
      scroll: false,
    });
  }

  onSubmit(event) {
    if (!(event.target instanceof Element)) return;

    const form = event.target.closest('[data-collection-filter-form]');
    if (!form || !this.contains(form)) return;

    event.preventDefault();
    this.render(this.getFormUrl(form), {
      history: 'push',
      focus: { type: 'status' },
      scroll: false,
    });
  }

  onClick(event) {
    if (!(event.target instanceof Element)) return;

    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }

    const link = event.target.closest('[data-collection-filter-link], .pagination a');
    if (!link || !this.contains(link) || (link.target && link.target !== '_self')) return;

    const url = this.getCleanUrl(link.href);
    if (url.origin !== window.location.origin) return;

    event.preventDefault();
    const isPagination = link.closest('.pagination') !== null;
    if (!isPagination) url.searchParams.delete('page');
    this.render(url, {
      history: 'push',
      focus: { type: 'status' },
      scroll: isPagination,
    });
  }

  onPopState() {
    this.render(this.getCleanUrl(window.location.href), {
      history: 'replace',
      focus: null,
      scroll: false,
    });
  }

  getFormUrl(form) {
    const url = this.getCleanUrl(form.action);
    url.search = new URLSearchParams(new FormData(form)).toString();
    url.searchParams.delete('page');
    return this.getCleanUrl(url);
  }

  getCleanUrl(value) {
    const url = new URL(value, window.location.origin);
    url.searchParams.delete('section_id');
    const supportedSorts = [
      'best-selling', 'title-ascending', 'title-descending', 'price-ascending', 'price-descending',
    ];
    const sort = url.searchParams.get('sort_by') || this.dataset.defaultSort;
    url.searchParams.set('sort_by', supportedSorts.includes(sort) ? sort : 'title-ascending');
    url.hash = '';
    return url;
  }

  getRenderUrl(value) {
    return this.getUrlInContext(value, this.dataset.renderUrl);
  }

  getHistoryUrl(value) {
    return this.getUrlInContext(value, this.dataset.historyUrl);
  }

  getUrlInContext(value, context) {
    const sourceUrl = this.getCleanUrl(value);
    if (!context) return sourceUrl;

    const contextUrl = this.getCleanUrl(context);
    contextUrl.search = sourceUrl.search;
    return contextUrl;
  }

  shouldHydrateFromLocation() {
    if (this.dataset.featuredCollectionId) return true;
    if (this.getCleanUrl(window.location.href).searchParams.get('sort_by') !== this.dataset.nativeSort) {
      return true;
    }
    const renderUrl = this.getRenderUrl(window.location.href);
    const historyUrl = this.getHistoryUrl(window.location.href);
    if (renderUrl.pathname === historyUrl.pathname) return false;

    return Array.from(new URL(window.location.href).searchParams.keys()).some(
      (key) => key === 'page' || key === 'sort_by' || key.startsWith('filter.')
    );
  }

  getFocusDescriptor(control) {
    if (control.matches('[data-collection-sort]')) return { type: 'sort' };

    return {
      type: 'filter',
      name: control.name,
      value: control.value,
    };
  }

  async fetchProductBatch(sourceUrl, page, featuredCollectionId, signal) {
    const url = new URL(sourceUrl);
    url.searchParams.set('section_id', 'catalog-products');
    url.searchParams.set('page', String(page));
    const response = await fetch(url.href, { credentials: 'same-origin', signal });
    if (!response.ok) throw new Error(`Catalog product request failed: ${response.status}`);

    const document = new DOMParser().parseFromString(await response.text(), 'text/html');
    const batch = document.querySelector('[data-catalog-products]');
    if (!batch || batch.dataset.featuredCollectionId !== featuredCollectionId) {
      throw new Error('Catalog product response was incomplete.');
    }

    const total = Number(batch.dataset.totalProducts);
    const pages = Math.max(1, Number(batch.dataset.pages));
    const cards = Array.from(batch.querySelectorAll('[data-catalog-product-id]'));
    if (
      !Number.isInteger(total) || total < 0 || total > 25000 ||
      pages !== Math.max(1, Math.ceil(total / 250)) ||
      Number(batch.dataset.currentPage) !== page ||
      cards.length !== Math.min(250, Math.max(0, total - (page - 1) * 250)) ||
      cards.some((card) => !card.dataset.catalogProductId ||
        !['true', 'false'].includes(card.dataset.catalogFeatured))
    ) {
      throw new Error('Catalog product pagination was inconsistent.');
    }
    return { total, pages, cards };
  }

  async getFeaturedProducts(sourceUrl, featuredCollectionId, signal) {
    const url = this.getRenderUrl(sourceUrl);
    url.searchParams.delete('page');
    url.searchParams.sort();
    const key = `${url.href}|${featuredCollectionId}`;
    if (this.productCache?.key === key) return this.productCache;
    this.productCache = null;

    const first = await this.fetchProductBatch(url, 1, featuredCollectionId, signal);
    const batches = [first.cards];
    let nextPage = 2;
    let failed = false;
    const loadPages = async () => {
      try {
        while (!failed && !signal.aborted && nextPage <= first.pages) {
          const page = nextPage++;
          const batch = await this.fetchProductBatch(url, page, featuredCollectionId, signal);
          if (batch.total !== first.total || batch.pages !== first.pages) {
            throw new Error('Catalog products changed during loading.');
          }
          batches[page - 1] = batch.cards;
        }
      } catch (error) {
        failed = true;
        throw error;
      }
    };
    await Promise.all(Array.from({ length: Math.min(3, first.pages - 1) }, loadPages));
    signal.throwIfAborted();

    const featured = [];
    const regular = [];
    const ids = new Set();
    for (const card of batches.flat()) {
      const id = card.dataset.catalogProductId;
      if (ids.has(id)) throw new Error('Catalog products changed during loading.');
      ids.add(id);
      (card.dataset.catalogFeatured === 'true' ? featured : regular).push(card);
    }
    this.productCache = { key, total: first.total, cards: featured.concat(regular) };
    return this.productCache;
  }

  async promoteFeaturedProducts(element, content, sourceUrl, signal) {
    const featuredCollectionId = element.dataset.featuredCollectionId;
    const grid = content.querySelector('[data-catalog-grid]');
    if (!featuredCollectionId || !grid) {
      this.productCache = null;
      return;
    }

    const products = await this.getFeaturedProducts(sourceUrl, featuredCollectionId, signal);
    const size = Number(element.dataset.pageSize);
    const page = Number(element.dataset.currentPage);
    if (
      products.total !== Number(element.dataset.totalProducts) ||
      !Number.isInteger(size) || size < 1 ||
      !Number.isInteger(page) || page < 1
    ) {
      this.productCache = null;
      throw new Error('Catalog page did not match the product results.');
    }
    const cards = products.cards.slice((page - 1) * size, page * size);
    if (cards.length === 0 && products.total > 0) {
      throw new Error('Catalog page was outside the product results.');
    }
    signal.throwIfAborted();
    grid.replaceChildren(...cards.map((card) => card.cloneNode(true)));
  }

  async render(sourceUrl, options) {
    if (this.abortController) this.abortController.abort();

    const controller = new AbortController();
    this.abortController = controller;
    this.setAttribute('aria-busy', 'true');
    this.announce(this.dataset.loadingText);

    const renderUrl = this.getRenderUrl(sourceUrl);
    const historyUrl = this.getHistoryUrl(sourceUrl);
    const requestUrl = new URL(renderUrl.href);
    requestUrl.searchParams.set('section_id', this.dataset.sectionId);

    try {
      const response = await fetch(requestUrl.href, {
        credentials: 'same-origin',
        headers: {
          Accept: 'text/html',
          'X-Requested-With': 'XMLHttpRequest',
        },
        signal: controller.signal,
      });

      if (!response.ok) throw new Error(`Collection section request failed: ${response.status}`);

      const nextDocument = new DOMParser().parseFromString(await response.text(), 'text/html');
      const nextElement = Array.from(
        nextDocument.querySelectorAll('collection-filters[data-section-id]')
      ).find((element) => element.dataset.sectionId === this.dataset.sectionId);
      const nextContent = nextElement
        ? nextElement.querySelector('[data-collection-content]')
        : null;
      const currentContent = this.querySelector('[data-collection-content]');

      if (!nextElement || !nextContent || !currentContent) {
        throw new Error('Collection section response was incomplete.');
      }
      if (controller.signal.aborted || !this.isConnected) return;

      // A data request can fail independently of the native collection section.
      // In that case commit its complete native grid, never a partial batch list.
      try {
        await this.promoteFeaturedProducts(nextElement, nextContent, renderUrl, controller.signal);
      } catch (error) {
        if (controller.signal.aborted) return;
        this.productCache = null;
      }
      if (controller.signal.aborted || !this.isConnected) return;

      currentContent.replaceWith(nextContent);
      for (const key of [
        'resultsText', 'featuredCollectionId', 'defaultSort', 'nativeSort',
        'pageSize', 'currentPage', 'totalProducts',
      ]) {
        this.dataset[key] = nextElement.dataset[key] || '';
      }
      this.enhanceControls();

      if (options.history === 'push' && historyUrl.href !== window.location.href) {
        window.history.pushState({}, '', historyUrl.href);
      } else if (options.history === 'replace' && historyUrl.href !== window.location.href) {
        window.history.replaceState({}, '', historyUrl.href);
      }

      this.restoreFocus(options.focus, options.scroll);
      this.announce(this.dataset.resultsText);
    } catch (error) {
      if (controller.signal.aborted) return;
      // Initial hydration must not repeatedly reload when section loading fails.
      if (!options.initial) window.location.assign(renderUrl.href);
    } finally {
      if (this.abortController === controller) {
        this.abortController = null;
        this.removeAttribute('aria-busy');
      }
    }
  }

  restoreFocus(descriptor, shouldScroll) {
    window.requestAnimationFrame(() => {
      let target = null;

      if (descriptor && descriptor.type === 'filter') {
        target = Array.from(this.querySelectorAll('[data-collection-filter-input]')).find(
          (input) => input.name === descriptor.name && input.value === descriptor.value
        );
      } else if (descriptor && descriptor.type === 'sort') {
        target = this.querySelector('[data-collection-sort]');
      } else if (descriptor && descriptor.type === 'status') {
        target = this.querySelector('[data-collection-status]');
      }

      if (target) target.focus({ preventScroll: true });
      if (shouldScroll) this.scrollIntoView({ block: 'start' });
    });
  }

  announce(message) {
    const liveRegion = this.querySelector('[data-collection-live-region]');
    if (!liveRegion || !message) return;

    if (this.announcementFrame) window.cancelAnimationFrame(this.announcementFrame);
    liveRegion.textContent = '';
    this.announcementFrame = window.requestAnimationFrame(() => {
      liveRegion.textContent = message;
      this.announcementFrame = null;
    });
  }
}
customElements.define('collection-filters', CollectionFilters);

// Related products: check later catalog pages before settling for generic cards.
class RelatedProducts extends HTMLElement {
  connectedCallback() {
    this.grid = this.querySelector('[data-related-grid]');
    if (!this.grid) return;

    this.limit = Math.max(3, Math.min(12, Number(this.dataset.limit) || 3));
    this.products = new Map();
    this.mergeProducts(this.grid.children);

    if (this.dataset.nextPage && this.needsMoreProducts()) {
      this.controller?.abort();
      this.controller = new AbortController();
      this.loadMoreProducts(this.controller.signal);
    }
  }

  disconnectedCallback() {
    this.controller?.abort();
  }

  mergeProducts(cards) {
    for (const card of cards) {
      const id = card.dataset.relatedProductId;
      if (!id || id === this.dataset.productId) continue;

      const existing = this.products.get(id);
      if (!existing || Number(card.dataset.relatedPriority) < Number(existing.dataset.relatedPriority)) {
        this.products.set(id, card);
      }
    }
  }

  needsMoreProducts() {
    if (this.products.size < this.limit) return true;
    if (!this.dataset.categoryId) return false;

    const preferredCount = [...this.products.values()].filter(
      (card) => Number(card.dataset.relatedPriority) < 3
    ).length;
    return preferredCount < this.limit;
  }

  async loadMoreProducts(signal) {
    let nextPage = this.dataset.nextPage;
    const visited = new Set();

    try {
      while (nextPage && this.needsMoreProducts()) {
        const url = new URL(nextPage, window.location.href);
        if (url.origin !== window.location.origin || visited.has(url.href)) break;
        visited.add(url.href);
        url.searchParams.set('section_id', this.dataset.sectionId);

        const response = await fetch(url.href, { signal });
        if (!response.ok) break;

        const document = new DOMParser().parseFromString(await response.text(), 'text/html');
        if (signal.aborted) return;
        const section = [...document.querySelectorAll('related-products')].find(
          (element) => element.dataset.sectionId === this.dataset.sectionId
        );
        const grid = section?.querySelector('[data-related-grid]');
        if (!grid) break;

        this.mergeProducts(grid.children);
        nextPage = section.dataset.nextPage;
      }
    } catch {
      // Keep the server-rendered cards if an additional catalog page cannot load.
    }

    if (signal.aborted || !this.isConnected) return;
    const cards = [...this.products.values()]
      .sort((a, b) => Number(a.dataset.relatedPriority) - Number(b.dataset.relatedPriority))
      .slice(0, this.limit);
    this.grid.replaceChildren(...cards);
    this.hidden = cards.length === 0;
  }
}
customElements.define('related-products', RelatedProducts);

// Address form: preselect the saved country
(() => {
  document.querySelectorAll('select[data-default]').forEach((select) => {
    const value = select.dataset.default;
    if (value) select.value = value;
  });
})();

// Product gallery: thumbnail click swaps the featured image
(() => {
  document.querySelectorAll('[data-product-gallery]').forEach((gallery) => {
    const mainImage = gallery.querySelector('[data-main-image]');
    const mainImageFrame = gallery.querySelector('.product__main-image');
    const thumbs = gallery.querySelectorAll('[data-media-id]');
    if (!mainImage || thumbs.length === 0) return;

    thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => {
        const img = thumb.querySelector('img');
        if (!img) return;
        const aspectRatio = Number.parseFloat(img.dataset.aspectRatio);
        if (mainImageFrame && Number.isFinite(aspectRatio) && aspectRatio > 0) {
          mainImageFrame.style.setProperty('--product-media-aspect-ratio', String(aspectRatio));
          mainImageFrame.style.setProperty(
            '--product-media-frame-width',
            `${Math.min(aspectRatio, 1) * 100}%`
          );
        }
        mainImage.src = img.dataset.fullSrc || img.src;
        mainImage.srcset = img.dataset.fullSrcset || '';
        mainImage.alt = img.alt;
        mainImage.style.objectPosition =
          img.dataset.focalPoint || img.style.objectPosition || '50% 50%';
        thumbs.forEach((t) => t.setAttribute('aria-current', 'false'));
        thumb.setAttribute('aria-current', 'true');
      });
    });
  });
})();
